import React from "react";
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";

interface ProcessTableSkeletonProps {
  /** Number of placeholder rows. Should match `pageSize` so the skeleton is the same height as the real table. */
  rows?: number;
  history?: boolean;
}

/**
 * Skeleton that mirrors the column layout and row height of ProcessesList.
 *
 * Live view columns  (history=false):  Processo | Attività | Durata | Attesa | Lavoro | Priorità | Partecipanti | Azioni
 * History columns    (history=true):   Processo | Durata | Priorità | Partecipanti | Collapse
 *
 * Row height matches the virtualised table `itemSize={120}`.
 */
const ProcessTableSkeleton: React.FC<ProcessTableSkeletonProps> = ({
  rows = 8,
  history = false,
}) => {
  const theme = useTheme();
  const headerBg =
    theme.palette.mode === "dark" ? theme.palette.grey[900] : "#eeeeee";

  // Column widths match ProcessesList exactly
  const liveColumns = [
    { key: "process",      w: "15%" },
    { key: "activity",     w: "15%" },
    { key: "duration",     w: "15%" },
    { key: "waiting",      w: "15%" },
    { key: "working",      w: "15%" },
    { key: "priority",     w: "10%" },
    { key: "participants", w: "10%" },
    { key: "actions",      w: "5%"  },
  ];

  const historyColumns = [
    { key: "process",      w: "15%" },
    { key: "duration",     w: "15%" },
    { key: "priority",     w: "10%" },
    { key: "participants", w: "20%" },
    { key: "collapse",     w: "5%"  },
  ];

  const columns = history ? historyColumns : liveColumns;
  const ROW_HEIGHT = 120; // matches VirtualTable itemSize

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Table
        sx={{
          width: "100%",
          tableLayout: "fixed",
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                sx={{
                  width: col.w,
                  backgroundColor: headerBg,
                  borderBottom: "none",
                  py: 1.5,
                }}
              >
                <Skeleton variant="text" width="60%" height={18} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {Array.from({ length: rows }).map((_, rowIdx) => (
            <TableRow
              key={rowIdx}
              sx={{
                height: ROW_HEIGHT,
                "& td": { borderBottom: `1px solid ${theme.palette.divider}` },
              }}
            >
              {/* Processo — avatar + 2 righe di testo */}
              <TableCell sx={{ width: columns[0].w }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Skeleton variant="circular" width={36} height={36} sx={{ flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Skeleton variant="text" width="80%" height={16} />
                    <Skeleton variant="text" width="55%" height={13} sx={{ mt: 0.5 }} />
                  </Box>
                </Stack>
              </TableCell>

              {/* Attività — solo live */}
              {!history && (
                <TableCell>
                  <Skeleton variant="text" width="70%" height={16} />
                  <Skeleton variant="text" width="40%" height={13} sx={{ mt: 0.5 }} />
                </TableCell>
              )}

              {/* Durata processo */}
              <TableCell>
                <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
              </TableCell>

              {/* Attesa + Lavoro — solo live */}
              {!history && (
                <>
                  <TableCell>
                    <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 3 }} />
                  </TableCell>
                </>
              )}

              {/* Priorità */}
              <TableCell>
                <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: 4 }} />
              </TableCell>

              {/* Partecipanti — avatar sovrapposti */}
              <TableCell>
                <Stack direction="row" sx={{ "& > span": { ml: -0.75 } }}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton
                      key={i}
                      variant="circular"
                      width={30}
                      height={30}
                      sx={{
                        border: `2px solid ${theme.palette.background.paper}`,
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </Stack>
              </TableCell>

              {/* Azioni (live) o Collapse (history) */}
              <TableCell>
                <Skeleton variant="circular" width={30} height={30} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ProcessTableSkeleton;
